import { ReactNode } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { css } from '@emotion/react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { CalloutBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import type { UseTooltipProps } from '@frontend/design-system';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { useTourHighlight } from './tour-highlight-context';

export type TourHighlightProps = {
  children: ReactNode;
  containerCss?: Interpolation<Theme>;
  dotPosition?: Partial<Record<'top' | 'right' | 'bottom' | 'left', number | string>>;
  message?: string;
  dotId: string;
  placement?: UseTooltipProps['placement'];
  width?: number;
};

const getArrowPosition = (placement: UseTooltipProps['placement']) => {
  switch (placement) {
    case 'bottom':
      return 'top' as const;
    case 'left':
      return 'right' as const;
    case 'right':
      return 'left' as const;
    case 'top':
    default:
      return 'bottom' as const;
  }
};

const DOT_SIZE = 12;

/**
 * Inner component that renders the tour highlight dot and callout.
 * Kept separate so that `useFloating` (and its internal effects/state) is only
 * instantiated when the tour is actually active, preventing spurious re-render
 * cycles in the wrapping component tree.
 */
const TourHighlightActive = ({
  children,
  containerCss,
  dotPosition,
  message,
  dotId,
  placement = 'top',
  width = 287,
}: TourHighlightProps) => {
  const { t } = useTranslation('phone');
  const { activeDotId, setActiveDotId, dismissDotId } = useTourHighlight();
  const calloutOffset = Number.parseFloat(theme.spacing(2).toString()) || 8;
  const { refs, floatingStyles } = useFloating({
    placement,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(calloutOffset), flip(), shift()],
  });

  const isOpen = activeDotId === dotId;

  const arrowPosition = getArrowPosition(placement);
  const translateX = dotPosition?.left != null ? '-50%' : '50%';
  const translateY = dotPosition?.bottom != null ? '50%' : '-50%';

  return (
    <div
      css={[
        {
          position: 'relative',
          display: 'inline-flex',
          width: 'max-content',
          maxWidth: '100%',
          overflow: 'visible',
          zIndex: isOpen ? theme.zIndex.modal : 'auto',
        },
        containerCss,
      ]}
    >
      {children}
      <div
        css={{
          position: 'absolute',
          top: dotPosition?.top ?? 0,
          right: dotPosition?.right ?? 0,
          bottom: dotPosition?.bottom,
          left: dotPosition?.left,
          width: DOT_SIZE,
          height: DOT_SIZE,
          transform: `translate(${translateX}, ${translateY})`,
          zIndex: theme.zIndex.modal,
        }}
      >
        <button
          type='button'
          aria-label={message}
          data-trackingid={teamPresenceTrackingId({ context: 'tour-highlight::dot' })}
          ref={refs.setReference}
          onClick={() => {
            setActiveDotId(dotId);
          }}
          css={{
            width: DOT_SIZE,
            height: DOT_SIZE,
            borderRadius: '50%',
            border: 0,
            backgroundColor: theme.colors.primary50,
            cursor: 'pointer',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            css={{
              position: 'fixed',
              zIndex: theme.zIndex.modal,
              width,
            }}
            style={floatingStyles}
          >
            <CalloutBanner
              arrowPosition={arrowPosition}
              description={message}
              actions={
                <Button
                  variant='tertiary'
                  size='small'
                  onClick={() => {
                    dismissDotId(dotId);
                    setActiveDotId(null);
                  }}
                  css={styles.actionBtn}
                  trackingId={teamPresenceTrackingId({ context: 'tour-highlight::done' })}
                >
                  {t('Done')}
                </Button>
              }
            />
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export const TourHighlight = ({
  children,
  containerCss,
  dotPosition,
  message,
  dotId,
  placement = 'top',
  width,
}: TourHighlightProps) => {
  const { isTourActive, dismissedDotIds } = useTourHighlight();

  if (!children || !isTourActive || !message || dismissedDotIds.has(dotId)) {
    return <>{children}</>;
  }

  return (
    <TourHighlightActive
      containerCss={containerCss}
      dotPosition={dotPosition}
      message={message}
      dotId={dotId}
      placement={placement}
      width={width}
    >
      {children}
    </TourHighlightActive>
  );
};

const styles = {
  actionBtn: css`
    color: var(--fabric-color-neutral-50);
    font-weight: var(--font-weight-regular);
    height: 20px;
    padding: 0;
  `,
};
